import * as THREE from '@phdsoft/three';
// The BVH is no longer used: sections are frustum-tested with precomputed bounding spheres (bvh.ts kept, unused).
// import { BVH } from './bvh';
// was: import { SoftwareDepthBuffer } from './software-depth-buffer';
// was: import { SoftwareDepthBuffer, TriangleTest, VisibleReason } from './software-depth-buffer';
import { CoverageSubSamples, SoftwareDepthBuffer, TriangleTest, VisibleReason } from './software-depth-buffer';
// was: import { CullingConfig, CullingStats, createDefaultCullingConfig, createEmptyStats } from './bvh.types';
// was: import { BackoffReason, CullingConfig, CullingStats, createDefaultCullingConfig, createEmptyStats } from './bvh.types';
import { BackoffReason, CullingConfig, CullingStats, TypeMeshTestMode, createDefaultCullingConfig, createEmptyStats } from './bvh.types';
import { materialOccludes } from './occluder-material';
import { CullSection, computeSectionLocalBox, isAttachedToScene } from './cull-section';

/** Fine-tier outcome for one mesh group of a section (its plates, or its type meshes). */
enum MeshGroupTest {
  /** A tested mesh was not Occluded (Visible or NoCoverage): the section stays. */
  Visible,
  /** Every tested mesh was Occluded (at least one tested). */
  Occluded,
  /** No mesh of the group was tested (hidden, outside the frustum or without triangles). */
  NoEvidence,
}

/** Per-section data precomputed at build and reused every frame. */
interface SectionState {
  section: CullSection;
  /** Union of LightSection geometry bounds in section.group-local space. */
  localBox: THREE.Box3;
  localSphere: THREE.Sphere;
  /** No geometry at all: never tested, never hidden. */
  empty: boolean;
  /** Squared distance of the world sphere centre to the camera (per frame). */
  distSq: number;
  /**
   * Squared distance, in section.group-local space, from the camera to the nearest point of localBox;
   * per frame. The sort key (OcclusionCuller.orderKey 'nearest').
   * A box that contains the camera has no nearest point: its key is distSq (was: 0, sorted first).
   */
  nearDistSq: number;
  /** Inverse of worldSeen (section.group.matrixWorld at build or at the last change seen by a cull). */
  inverseWorld: THREE.Matrix4;
  /** The section.group.matrixWorld inverseWorld was computed from; compared every cull. */
  worldSeen: THREE.Matrix4;
  /**
   * Screen-rect area (depth-buffer pixels², clamped) of the section box this frame, Infinity when
   * the box crosses the near or far plane. Set by the coarse test (or for pinned sections before
   * rasterizing); read by the occluder size gate.
   */
  rectPixels: number;
  /** Mesh-local bounding spheres of section.plates / section.typeMeshes (null: no geometry). */
  plateSpheres: (THREE.Sphere | null)[];
  typeSpheres: (THREE.Sphere | null)[];
  // was: /** Mesh-local bounding boxes of section.typeMeshes (null: no geometry), for typeMeshTest 'boxes'. */
  /** Mesh-local bounding boxes of section.typeMeshes (null: no geometry), for typeMeshTest 'boxes' / 'boxes-then-triangles'. */
  typeBoxes: (THREE.Box3 | null)[];
}

/** Mesh-local bounding sphere of a mesh's geometry (a copy), or null without position data. */
function localMeshSphere(mesh: THREE.Mesh): THREE.Sphere | null {
  const geometry = mesh.geometry as THREE.BufferGeometry | undefined;
  if (!geometry || !geometry.getAttribute('position')) {
    return null;
  }
  if (!geometry.boundingSphere) {
    geometry.computeBoundingSphere();
  }
  return geometry.boundingSphere ? geometry.boundingSphere.clone() : null;
}

/** Mesh-local bounding box of a mesh's geometry (a copy), or null without position data / when empty. */
function localMeshBox(mesh: THREE.Mesh): THREE.Box3 | null {
  const geometry = mesh.geometry as THREE.BufferGeometry | undefined;
  if (!geometry || !geometry.getAttribute('position')) {
    return null;
  }
  if (!geometry.boundingBox) {
    geometry.computeBoundingBox();
  }
  return geometry.boundingBox && !geometry.boundingBox.isEmpty() ? geometry.boundingBox.clone() : null;
}

/**
 * Instances / batches / skinned vertices are not bounded by the geometry's own bounds
 * (three.js bounds them separately): neither the frustum skip nor a geometry box applies.
 */
function isUnboundedByGeometry(mesh: THREE.Mesh): boolean {
  return !!((mesh as any).isInstancedMesh || (mesh as any).isBatchedMesh || (mesh as any).isSkinnedMesh);
}

/**
 * Section-level CPU occlusion culler.
 *
 * Unit of work = one light section (LightSection under a culler-owned
 * proxy group; Borders stay outside it). Per frame: skip sections that are not source-visible or are
 * outside the frustum (proxy shown), then front to back test each section
 * against the depth buffer built from the nearer sections that survived
 * (coarse tier: the section box's faces per pixel; fine tier: plates at triangle level,
 * then type meshes per config.typeMeshTest (default 'boxes-then-triangles': each type mesh's own box as a
 * fast reject, its triangles as the decision); triangle tests use the strided early-accept of
 * config.earlyAcceptStride) and rasterize only the plates of surviving sections, and of those
 * only sections whose box rect is at least config.occluderMinRectPixels and whose plates fit in
 * config.occluderTriangleBudget (the others are shown but do not occlude: under-occlusion only).
 * Front to back = ascending squared distance from the camera to the nearest point of the section box
 * (section space; a box containing the camera uses its centre distance), the sphere-centre distance
 * as tiebreak; OcclusionCuller.orderKey = 'centre'
 * (measurement only) restores the sphere-centre order.
 * A section is visible only where it is in front of the buffer by more than the occludee depth margin
 * (config.occludeeDepthMarginNdc / occludeeDepthMarginRelative); ties (T-joints) are hidden.
 * The culler writes only `proxy.visible`. The depth buffer is recreated at the next cull when
 * config.depthBufferResolution or config.coverageSubSamples (coverage mask, 1 = legacy) changes.
 *
 * Adaptive back-off: over the last config.backoffWindow culls, when the culled share of the
 * candidate triangles is below config.backoffMinCulledFraction ('share'), or the median cull
 * time exceeds what the hidden sections save — max(config.backoffMaxCullMs, mean culled sections ×
 * config.backoffSectionCostMs) — ('cost'), every proxy is shown and culling stops except for one
 * probe cull every config.backoffProbeInterval cull calls. A probe that the same rules would not
 * back off on (its own share, cull time and culled sections) resumes culling.
 * was: 'cost' = median above backoffMaxCullMs while the share is below backoffCostShareCeiling,
 * never at or above backoffNeverBelowShare — a triangle share that said nothing about the saving.
 */
export class OcclusionCuller {
  /**
   * Sort key of the front-to-back test order. 'nearest' (default): nearest point of the section box
   * (section space), the sphere-centre distance as tiebreak. 'centre': the sphere-centre distance only
   * (the order before 2026-10-26). Measurement only (A/B of the two orders); not part of CullingConfig.
   */
  static orderKey: 'nearest' | 'centre' = 'nearest';

  // was: private static readonly CULLING_FLAG = '__occlusionCulled'; — per-mesh flag bookkeeping removed, the culler writes only proxy.visible
  // was: private bvh: BVH; — frustum test is per-section bounding sphere now
  private depthBuffer: SoftwareDepthBuffer;
  private _config: CullingConfig;
  private _stats: CullingStats;
  // was: private allObjects: THREE.Mesh[] = []; private occluders: THREE.Mesh[] = []; — replaced by sections
  private sections: SectionState[] = [];
  /** Scratch list of the sections that reach the occlusion test this frame. */
  private readonly active: SectionState[] = [];

  // Reusable arrays for OBB corner computation to avoid allocations per frame
  private readonly obbCorners: THREE.Vector3[] = Array.from({ length: 8 }, () => new THREE.Vector3());
  /** obbCorners as flat xyz for SoftwareDepthBuffer.isOccludedBox. */
  private readonly boxCornerValues = new Float64Array(24);
  /** Screen-rect area of the box last tested by isOccludedByBox (Infinity: crosses the near / far plane). */
  private lastBoxRectPixels = Infinity;
  /** Plate triangles admitted under config.occluderTriangleBudget so far this cull. */
  private occluderTrianglesUsed = 0;
  // was: private readonly localBox: THREE.Box3 = new THREE.Box3(); — unused
  private readonly frustum = new THREE.Frustum();
  private readonly viewProjection = new THREE.Matrix4();
  private readonly cameraWorldPos = new THREE.Vector3();
  /** Scratch: the camera position in the section space of the section being keyed (phase 1). */
  private readonly localCam = new THREE.Vector3();
  /** Scratch: localCam clamped to the section's localBox (its nearest box point). */
  private readonly clampScratch = new THREE.Vector3();
  private readonly worldSphere = new THREE.Sphere();
  private readonly clipCorner = new THREE.Vector4();

  // Signature of the inputs of the last cull (camera matrices + per-section and
  // per-mesh visibility / occluder-material state). Lets idle safety frames skip
  // culling when nothing changed, while still catching app changes that never
  // call requestRender().
  private lastCameraSignature: Float64Array | null = null;
  // was: private lastMeshSignature: Uint8Array | null = null; — mesh words now also carry the mask version
  private lastMeshSignature: Uint32Array | null = null;
  private signatureLength = 0;

  // Adaptive back-off (spec 3.2 g): culled / candidate triangles of the last
  // config.backoffWindow real culls (ring buffers), and the backed-off state.
  private backedOff = false;
  private windowCulled = new Float64Array(0);
  private windowCandidates = new Float64Array(0);
  /** Sections hidden by each cull of the window (occlusionCulled), for the cost rule's budget. */
  private windowSections = new Float64Array(0);
  /** Cull times (ms, whole cull) of the same window, for the cost rule's median. */
  private windowMs = new Float64Array(0);
  /** Scratch copy of windowMs sorted for the median. */
  private windowMsSorted = new Float64Array(0);
  private backoffReason: BackoffReason | null = null;
  /**
   * Why the last Visible result of testMeshGroup kept the section (None is mapped to Untested);
   * null when it was a NoCoverage keep (counted in plateNoCoverage, not here).
   */
  private lastKeepReason: VisibleReason | null = null;
  private windowNext = 0;
  private windowFilled = 0;
  private callsSinceProbe = 0;

  /** Clock (ms) timing each real cull for the back-off cost rule; injectable so specs are deterministic. */
  private readonly now: () => number;

  // was: constructor() {
  constructor(options: { now?: () => number } = {}) {
    this.now = options.now ?? (() => performance.now());
    // was: this.bvh = new BVH();
    this._config = createDefaultCullingConfig();
    this._stats = createEmptyStats();
    // was: this.depthBuffer = new SoftwareDepthBuffer(this._config.depthBufferResolution);
    // was: this.depthBuffer = new SoftwareDepthBuffer(this._config.depthBufferResolution, OcclusionCuller.coverageSamples(this._config));
    this.depthBuffer = new SoftwareDepthBuffer(OcclusionCuller.bufferResolution(this._config), OcclusionCuller.coverageSamples(this._config));
  }

  /** The configured depth buffer resolution; Number(r) must be 256, anything else (invalid) is 128. */
  private static bufferResolution(config: CullingConfig): 128 | 256 {
    return Number(config.depthBufferResolution) === 256 ? 256 : 128;
  }

  /** The configured coverage sub-sample count; a value that is not 1, 2 or 4 falls back to 4. */
  private static coverageSamples(config: CullingConfig): CoverageSubSamples {
    const k = Number(config.coverageSubSamples);
    return k === 1 || k === 2 || k === 4 ? k : 4;
  }

  /**
   * Replace the depth buffer when the resolution or the coverage sub-sample count of the config no
   * longer match it (the buffer fixes both at construction). The new buffer has zeroed counters.
   */
  private syncDepthBuffer(): void {
    // was: const resolution = this._config.depthBufferResolution;
    const resolution = OcclusionCuller.bufferResolution(this._config);
    const coverage = OcclusionCuller.coverageSamples(this._config);
    if (this.depthBuffer.resolution !== resolution || this.depthBuffer.coverageSubSamples !== coverage) {
      this.depthBuffer = new SoftwareDepthBuffer(resolution, coverage);
    }
  }

  /**
   * Compute OBB (Oriented Bounding Box) corners in world space for a mesh.
   * Uses the geometry's local bounding box transformed by the mesh's world matrix.
   * This gives much tighter bounds than world-space AABB for rotated objects.
   */
  private computeOBBCorners(mesh: THREE.Mesh): THREE.Vector3[] {
    // Get geometry's local bounding box
    if (!mesh.geometry.boundingBox) {
      mesh.geometry.computeBoundingBox();
    }
    mesh.updateMatrixWorld();
    return this.boxCorners(mesh.geometry.boundingBox!, mesh.matrixWorld);
  }

  /** The 8 corners of a local box transformed by `matrix`, written into the reusable corner array. */
  private boxCorners(box: THREE.Box3, matrix: THREE.Matrix4): THREE.Vector3[] {
    const min = box.min;
    const max = box.max;
    this.obbCorners[0].set(min.x, min.y, min.z).applyMatrix4(matrix);
    this.obbCorners[1].set(min.x, min.y, max.z).applyMatrix4(matrix);
    this.obbCorners[2].set(min.x, max.y, min.z).applyMatrix4(matrix);
    this.obbCorners[3].set(min.x, max.y, max.z).applyMatrix4(matrix);
    this.obbCorners[4].set(max.x, min.y, min.z).applyMatrix4(matrix);
    this.obbCorners[5].set(max.x, min.y, max.z).applyMatrix4(matrix);
    this.obbCorners[6].set(max.x, max.y, min.z).applyMatrix4(matrix);
    this.obbCorners[7].set(max.x, max.y, max.z).applyMatrix4(matrix);
    return this.obbCorners;
  }

  /**
   * isOccludedOBB ignores corners outside the NDC depth range when it builds the
   * screen rect and the nearest depth, which under-estimates both when a box
   * crosses the near or far plane. Only trust an OBB test when every corner is
   * strictly inside the depth range; otherwise the box counts as visible. Also guards
   * isOccludedByBox (isOccludedBox applies the same rule itself).
   */
  private cornersInDepthRange(corners: THREE.Vector3[]): boolean {
    const v = this.clipCorner;
    for (const corner of corners) {
      v.set(corner.x, corner.y, corner.z, 1).applyMatrix4(this.viewProjection);
      if (v.w <= 0) {
        return false;
      }
      const ndcZ = v.z / v.w;
      if (ndcZ < -1 || ndcZ > 1) {
        return false;
      }
    }
    return true;
  }

  /** Heavy-mesh fallback of the fine tier (the mesh's own OBB); counted in meshOBBTested. */
  private isOccludedByOBB(corners: THREE.Vector3[]): boolean {
    // was: this._stats.occludeesTestedOBB++; — occludeesTestedOBB counts section-level box tests only
    this._stats.meshOBBTested++;
    return this.cornersInDepthRange(corners) && this.depthBuffer.isOccludedOBB(corners);
  }

  /**
   * Per-pixel box-face test (SoftwareDepthBuffer.isOccludedBox) of 8 world corners in
   * boxCorners order, behind the same depth-range guard as isOccludedByOBB. Not counted here:
   * the caller counts it (occludeesTestedOBB for section boxes, typeBoxesTested for type meshes).
   * Leaves the box's screen-rect area in lastBoxRectPixels (Infinity when the guard fails).
   */
  private isOccludedByBox(corners: THREE.Vector3[]): boolean {
    // was: this._stats.occludeesTestedOBB++; — counted by the callers now (section vs type-mesh boxes)
    if (!this.cornersInDepthRange(corners)) {
      this.lastBoxRectPixels = Infinity; // crosses the near / far plane: counts as large
      return false;
    }
    const occluded = this.depthBuffer.isOccludedBox(this.cornerValues(corners));
    // The occluder size gate reads this value: a spec stubbing isOccludedBox after real culls must keep it fresh (see the pose-sweep spec's coarseNeverCulls callThrough stub).
    this.lastBoxRectPixels = this.depthBuffer.lastBoxRectPixels; // reuses isOccludedBox's projection
    return occluded;
  }

  /** `corners` as flat xyz in the reusable boxCornerValues array. */
  private cornerValues(corners: THREE.Vector3[]): Float64Array {
    const values = this.boxCornerValues;
    for (let i = 0; i < 8; i++) {
      const corner = corners[i];
      values[i * 3] = corner.x;
      values[i * 3 + 1] = corner.y;
      values[i * 3 + 2] = corner.z;
    }
    return values;
  }

  /** Screen-rect area of a section's box (pinned sections, which skip the coarse test). */
  private sectionRectPixels(state: SectionState): number {
    const corners = this.boxCorners(state.localBox, state.section.source.group.matrixWorld);
    return this.depthBuffer.boxRectPixels(this.cornerValues(corners));
  }

  get config(): CullingConfig {
    return this._config;
  }

  get stats(): CullingStats {
    return this._stats;
  }

  /**
   * Take the sections to cull. Precomputes each section's local bounds (box and
   * sphere in section.group space). Every proxy starts visible.
   */
  build(sections: CullSection[]): void {
    this.restoreAll();
    this.sections = sections.map(section => {
      const localBox = computeSectionLocalBox(section.source, new THREE.Box3());
      const empty = localBox.isEmpty();
      const localSphere = new THREE.Sphere();
      if (!empty) {
        localBox.getBoundingSphere(localSphere);
      }
      return {
        section, localBox, localSphere, empty, distSq: 0, rectPixels: Infinity,
        nearDistSq: 0,
        inverseWorld: new THREE.Matrix4().copy(section.source.group.matrixWorld).invert(),
        worldSeen: section.source.group.matrixWorld.clone(),
        plateSpheres: section.plates.map(localMeshSphere),
        typeSpheres: section.typeMeshes.map(localMeshSphere),
        typeBoxes: section.typeMeshes.map(localMeshBox)
      };
    });
    this.restoreAll();

    this.signatureLength = 0;
    for (const state of this.sections) {
      this.signatureLength += 1 + state.section.plates.length + state.section.typeMeshes.length;
    }
    this.invalidateSignature();
    this.resetBackoff();
    this._stats = createEmptyStats();
    this._stats.totalObjects = this.sections.length;
  }

  /** Show every proxy (every stop path ends here). */
  restoreAll(): void {
    for (const state of this.sections) {
      if (state.section.proxy) {
        state.section.proxy.visible = true;
      }
    }
    this.invalidateSignature();
  }

  /**
   * A mesh may occlude only if the GPU really writes it into the depth buffer
   * this frame. Uses the mesh's current material (view-mode mask clones are
   * assigned to the mesh), so it must be evaluated per frame, not at build.
   */
  private isValidOccluder(mesh: THREE.Mesh): boolean {
    // Previously every mesh with a material occluded, including depthWrite=false / invisible / translucent ones.
    // const material = mesh.material as THREE.Material;
    // if (!material) {
    //   return false;
    // }
    // return true;
    return materialOccludes(mesh.material);
  }

  private getTriangleCount(mesh: THREE.Mesh): number {
    const geometry = mesh.geometry as THREE.BufferGeometry;
    if (!geometry) {
      return 0;
    }
    const position = geometry.getAttribute('position') as THREE.BufferAttribute;
    if (!position) {
      return 0;
    }
    const index = geometry.getIndex();
    return index ? index.count / 3 : position.count / 3;
  }

  /**
   * True when `object` and every ancestor are visible AND the chain ends at a
   * THREE.Scene. A detached chain (region reloaded/removed) is not rendered, so it
   * must neither occlude nor be culled.
   */
  private isParentChainVisible(object: THREE.Object3D | null): boolean {
    // Previously the walk stopped at null and treated a detached chain as visible:
    // while (current) { if (!current.visible) { return false; } current = current.parent; } return true;
    // was: a local walk (visible + isScene) — now the shared helper also used by CullingService
    return isAttachedToScene(object, true);
  }

  /**
   * True when `object` and every ancestor below `stop` (exclusive) are visible
   * and `object` really sits under `stop`. Reads only app-owned flags: the
   * proxy itself is `stop`, so the culler's own writes never feed back.
   */
  private isVisibleBelow(object: THREE.Object3D, stop: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object;
    while (current && current !== stop) {
      if (!current.visible) {
        return false;
      }
      current = current.parent;
    }
    return current === stop;
  }

  /**
   * App-visible section: the proxy's parent chain is visible, LightSection is
   * visible under the proxy and at least one plate is visible. Never cull on a
   * types-only or Borders-only footprint (SECTIONS mode, plates filtered out…).
   */
  private isSectionSourceVisible(section: CullSection, proxy: THREE.Group): boolean {
    const lightSectionGroup = section.source.lightSectionGroup;
    if (!lightSectionGroup || !this.isParentChainVisible(proxy.parent) || !this.isVisibleBelow(lightSectionGroup, proxy)) {
      return false;
    }
    for (const plate of section.plates) {
      if (this.isVisibleBelow(plate, proxy)) {
        return true;
      }
    }
    return false;
  }

  /** Forget the last-cull signature so the next cullIfChanged() always culls. */
  invalidateSignature(): void {
    this.lastCameraSignature = null;
    this.lastMeshSignature = null;
  }

  /**
   * For idle safety renders: cull only if the camera, any section's source
   * visibility or pinned state, any plate/type mesh visibility or mask version, or
   * any plate's occluder-material state changed since the last cull. Returns true if it culled. While backed off it
   * only culls on probe counts (shared with cull()).
   */
  cullIfChanged(camera: THREE.Camera): boolean {
    if (!this._config.enabled) {
      // Stop path (like cull()): nothing may stay hidden while culling is off.
      // was: return false; — without restoring, proxies hidden by the last cull stayed hidden
      this.restoreAll();
      return false;
    }
    if (this.backedOff) {
      // Backed off: no-op except on probe counts (shared with cull()).
      if (!this.takeProbe()) {
        return false;
      }
      // was: this.runCull(camera); this.updateBackoff();
      this.updateBackoff(this.timedCull(camera));
      return true;
    }
    camera.updateMatrixWorld();
    if (this.signatureMatches(camera)) {
      return false;
    }
    // was: this.cull(camera);
    // was: this.runCull(camera); this.updateBackoff();
    this.updateBackoff(this.timedCull(camera));
    return true;
  }

  /**
   * Visit every signature word in a fixed order: per section one word
   * (proxy parent chain | LightSection visible under the proxy | pinned), then one word
   * per plate (visible under the proxy | materialOccludes | mask version), then one per
   * type mesh (visible under the proxy | mask version).
   * Sections without a proxy are never culled, so their words are constant 0.
   */
  private forEachSignatureByte(visit: (offset: number, bits: number) => boolean): boolean {
    let offset = 0;
    for (const state of this.sections) {
      const section = state.section;
      const proxy = section.proxy;
      const lightSectionGroup = section.source.lightSectionGroup;
      const sectionBits = !proxy ? 0
        : (this.isParentChainVisible(proxy.parent) ? 1 : 0)
          | (lightSectionGroup && this.isVisibleBelow(lightSectionGroup, proxy) ? 2 : 0)
          | (this.isSectionPinned(section) ? 4 : 0);
      if (!visit(offset++, sectionBits)) {
        return false;
      }
      for (const plate of section.plates) {
        // was: const bits = !proxy ? 0 : (this.isVisibleBelow(plate, proxy) ? 1 : 0) | (this.isValidOccluder(plate) ? 2 : 0);
        const bits = !proxy ? 0
          : OcclusionCuller.withMaskVersion(plate,
            (this.isVisibleBelow(plate, proxy) ? 1 : 0) | (this.isValidOccluder(plate) ? 2 : 0));
        if (!visit(offset++, bits)) {
          return false;
        }
      }
      for (const mesh of section.typeMeshes) {
        // was: const bits = !proxy ? 0 : (this.isVisibleBelow(mesh, proxy) ? 1 : 0);
        const bits = !proxy ? 0 : OcclusionCuller.withMaskVersion(mesh, this.isVisibleBelow(mesh, proxy) ? 1 : 0);
        if (!visit(offset++, bits)) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * `bits` (< 16) plus the mesh's lightsection mask version (userData.__lsMaskVersion,
   * bumped by lightsection-mask on every mask change) as one unsigned 32-bit word.
   * Mask changes alter what a mesh rasterizes / covers but never call requestRender.
   */
  private static withMaskVersion(mesh: THREE.Mesh, bits: number): number {
    const version = (mesh.userData?.__lsMaskVersion as number) ?? 0;
    return (bits | ((version & 0x0fffffff) << 4)) >>> 0;
  }

  /**
   * A section with any plate or type mesh on a selection-outline layer
   * (config.pinnedLayerMask) is pinned: never hidden, still an occluder. The outline
   * mask render draws those meshes through other geometry, and three.js renders
   * nothing under an invisible proxy.
   */
  private isSectionPinned(section: CullSection): boolean {
    const mask = this._config.pinnedLayerMask;
    if (!mask) {
      return false;
    }
    for (const plate of section.plates) {
      if ((plate.layers.mask & mask) !== 0) {
        return true;
      }
    }
    for (const mesh of section.typeMeshes) {
      if ((mesh.layers.mask & mask) !== 0) {
        return true;
      }
    }
    return false;
  }

  private signatureMatches(camera: THREE.Camera): boolean {
    const cam = this.lastCameraSignature;
    const meshes = this.lastMeshSignature;
    if (!cam || !meshes || meshes.length !== this.signatureLength) {
      return false;
    }
    const world = camera.matrixWorld.elements;
    const projection = camera.projectionMatrix.elements;
    for (let i = 0; i < 16; i++) {
      if (cam[i] !== world[i] || cam[16 + i] !== projection[i]) {
        return false;
      }
    }
    return this.forEachSignatureByte((offset, bits) => meshes[offset] === bits);
  }

  private recordSignature(camera: THREE.Camera): void {
    const cam = this.lastCameraSignature ?? new Float64Array(32);
    cam.set(camera.matrixWorld.elements, 0);
    cam.set(camera.projectionMatrix.elements, 16);
    this.lastCameraSignature = cam;

    let meshes = this.lastMeshSignature;
    if (!meshes || meshes.length !== this.signatureLength) {
      // was: meshes = new Uint8Array(this.signatureLength);
      meshes = new Uint32Array(this.signatureLength);
    }
    const target = meshes;
    this.forEachSignatureByte((offset, bits) => {
      target[offset] = bits;
      return true;
    });
    this.lastMeshSignature = meshes;
  }

  /** Visible (under the proxy) plate + type-mesh triangles of a section. */
  private visibleTriangles(section: CullSection, proxy: THREE.Group): number {
    let total = 0;
    for (const plate of section.plates) {
      if (this.isVisibleBelow(plate, proxy)) {
        total += this.getTriangleCount(plate);
      }
    }
    for (const mesh of section.typeMeshes) {
      if (this.isVisibleBelow(mesh, proxy)) {
        total += this.getTriangleCount(mesh);
      }
    }
    return total;
  }

  /**
   * Is the whole section hidden by the current depth buffer?
   * Coarse tier: the faces of the section's box (LightSection bounds × group
   * matrixWorld), tested per pixel (isOccludedBox). Box occluded ⇒ everything in it is occluded.
   * Fine tier: every visible, in-frustum plate at triangle level (testMeshOcclusion); a plate
   * that is not Occluded (Visible or NoCoverage) keeps the section. Then the type meshes per
   * config.typeMeshTest: 'triangles' at triangle level, 'boxes' by each mesh's own box,
   * 'boxes-then-triangles' by its own box first and, when the box is not occluded, at triangle
   * level, 'none' not at all. Culled only if no tested mesh kept the section and at least one was Occluded.
   */
  private isSectionOccluded(state: SectionState, proxy: THREE.Group): boolean {
    const stats = this._stats;
    const coarseStart = performance.now();
    const corners = this.boxCorners(state.localBox, state.section.source.group.matrixWorld);
    // was: const boxOccluded = this.isOccludedByOBB(corners); — nearest-corner depth over the screen rect
    const boxOccluded = this.isOccludedByBox(corners);
    stats.occludeesTestedOBB++;
    state.rectPixels = this.lastBoxRectPixels; // for the occluder size gate if the section survives
    stats.coarseTimeMs += performance.now() - coarseStart;
    if (boxOccluded) {
      stats.coarseCulled++;
      return true;
    }

    // was: a per-call testMeshes closure (boolean) sharing an anyOccluded flag across plates and
    // type meshes — now testMeshGroup (tri-state per group, same decisions) for the instrumentation.
    const plateStart = performance.now();
    // was: this.testMeshGroup(state.section.plates, state.plateSpheres, proxy, true);
    // was: const plates = this.testMeshGroup(state.section.plates, state.plateSpheres, null, proxy, true);
    const plates = this.testMeshGroup(state.section.plates, state.plateSpheres, null, 'triangles', proxy, true);
    const typeStart = performance.now();
    stats.plateTestTimeMs += typeStart - plateStart;
    if (plates === MeshGroupTest.Visible) {
      this.countKeepReason();
      return false;
    }
    const mode = this._config.typeMeshTest;
    let types = MeshGroupTest.NoEvidence; // 'none': type meshes are not tested
    if (mode !== 'none') {
      // was: const types = this.testMeshGroup(state.section.typeMeshes, state.typeSpheres, proxy, false);
      // was: types = this.testMeshGroup(state.section.typeMeshes, state.typeSpheres,
      //   mode === 'boxes' ? state.typeBoxes : null, proxy, false);
      types = this.testMeshGroup(state.section.typeMeshes, state.typeSpheres,
        mode === 'boxes' || mode === 'boxes-then-triangles' ? state.typeBoxes : null, mode, proxy, false);
      stats.typeTestTimeMs += performance.now() - typeStart;
    }
    if (types === MeshGroupTest.Visible) {
      this.countKeepReason();
      if (plates === MeshGroupTest.Occluded) {
        stats.rescuedByTypes++; // plates hidden, a type mesh keeps the section
      }
      return false;
    }
    // was: return anyOccluded; — at least one tested mesh (plate or type) occluded
    if (plates === MeshGroupTest.Occluded || types === MeshGroupTest.Occluded) {
      stats.fineCulled++;
      return true;
    }
    return false;
  }

  /** Adds the last Visible keep (lastKeepReason) to keptByDepth / keptByEmptyPixel / keptUntested. */
  private countKeepReason(): void {
    const stats = this._stats;
    switch (this.lastKeepReason) {
      case VisibleReason.Depth: stats.keptByDepth++; break;
      case VisibleReason.EmptyPixel: stats.keptByEmptyPixel++; break;
      case VisibleReason.Untested: stats.keptUntested++; break;
      default: break; // NoCoverage keep: counted in plateNoCoverage
    }
  }

  /**
   * Fine-tier test of one mesh group (a section's plates or its type meshes): Visible as soon
   * as a visible, in-frustum mesh with triangles is not Occluded (NoCoverage included: no
   * evidence keeps the section); else Occluded when at least one mesh was Occluded; else
   * NoEvidence (nothing tested). `countNoCoverage` adds NoCoverage meshes to stats.plateNoCoverage.
   * `mode` (the caller's, not read from the config: plates pass 'triangles') with `boxes`
   * (mesh-local boxes) picks the per-mesh test; meshes their geometry box does not bound
   * (instanced / batched / skinned) always keep the triangle test:
   * - 'boxes': each mesh by its own world box (isOccludedBox) instead of its triangles;
   * - 'boxes-then-triangles': meshes within maxTrianglesPerOccludeeTest by their own world box
   *   first (box occluded ⇒ Occluded, no triangle test); a box that is not occluded hands the
   *   decision to the triangle test (Visible / Occluded / NoCoverage, as in 'triangles'), counted in
   *   stats.typeTrianglesAfterBox and occludeesTestedGeometry; lastKeepReason comes from that test;
   * - otherwise (or without `boxes`): triangle level.
   * Heavy meshes (over maxTrianglesPerOccludeeTest) use their own OBB in every mode but 'boxes'.
   */
  // was: (meshes, spheres, proxy, countNoCoverage) — triangle level only
  // was: (meshes, spheres, boxes, proxy, countNoCoverage) — the box branch implied typeMeshTest 'boxes'
  private testMeshGroup(
    meshes: THREE.Mesh[], spheres: (THREE.Sphere | null)[], boxes: (THREE.Box3 | null)[] | null,
    mode: TypeMeshTestMode, proxy: THREE.Group, countNoCoverage: boolean
  ): MeshGroupTest {
    const maxOccludeeTris = this._config.maxTrianglesPerOccludeeTest;
    let anyOccluded = false;
    for (let i = 0; i < meshes.length; i++) {
      const mesh = meshes[i];
      if (!this.isVisibleBelow(mesh, proxy)) {
        continue;
      }
      if (this.isOutsideFrustum(mesh, spheres[i])) {
        continue; // three.js never draws it: no evidence either way
      }
      const triCount = this.getTriangleCount(mesh);
      if (triCount === 0) {
        continue; // draws nothing
      }
      let result: TriangleTest;
      this.depthBuffer.lastVisibleReason = VisibleReason.None; // set by the tests below at a Visible answer
      const box = boxes ? boxes[i] : null;
      // was: if (box && !isUnboundedByGeometry(mesh)) { — boxes implied 'boxes'
      if (box && mode === 'boxes' && !isUnboundedByGeometry(mesh)) {
        // Own world box, faces per pixel: box ⊇ mesh, so "box occluded" implies "mesh occluded".
        mesh.updateMatrixWorld();
        result = this.isOccludedByBox(this.boxCorners(box, mesh.matrixWorld)) ? TriangleTest.Occluded : TriangleTest.Visible;
        this._stats.typeBoxesTested++;
      } else if (triCount > maxOccludeeTris) {
        // Heavy mesh: its own OBB (conservative), as before.
        result = this.isOccludedByOBB(this.computeOBBCorners(mesh)) ? TriangleTest.Occluded : TriangleTest.Visible;
      } else if (box && mode === 'boxes-then-triangles' && !isUnboundedByGeometry(mesh)) {
        // Box as a fast reject (box ⊇ mesh: box occluded ⇒ mesh occluded), triangles as the decision.
        mesh.updateMatrixWorld();
        const boxOccluded = this.isOccludedByBox(this.boxCorners(box, mesh.matrixWorld));
        this._stats.typeBoxesTested++;
        if (boxOccluded) {
          result = TriangleTest.Occluded;
        } else {
          this._stats.typeTrianglesAfterBox++;
          this.depthBuffer.lastVisibleReason = VisibleReason.None; // the triangle test's reason, not the box's
          result = this.depthBuffer.testMeshOcclusion(mesh, maxOccludeeTris, this._config.earlyAcceptStride);
          this._stats.occludeesTestedGeometry++;
        }
      } else {
        // was: occluded = this.depthBuffer.isOccludedMesh(mesh, maxOccludeeTris); — same test, tri-state
        // was: result = this.depthBuffer.testMeshOcclusion(mesh, maxOccludeeTris); — strided early-accept, same result
        result = this.depthBuffer.testMeshOcclusion(mesh, maxOccludeeTris, this._config.earlyAcceptStride);
        this._stats.occludeesTestedGeometry++;
      }
      if (result !== TriangleTest.Occluded) {
        if (result === TriangleTest.NoCoverage && countNoCoverage) {
          this._stats.plateNoCoverage++;
        }
        if (result === TriangleTest.Visible) {
          const reason = this.depthBuffer.lastVisibleReason;
          this.lastKeepReason = reason === VisibleReason.None ? VisibleReason.Untested : reason; // OBB fallback / near-far guard
        } else {
          this.lastKeepReason = null;
        }
        return MeshGroupTest.Visible;
      }
      anyOccluded = true;
    }
    return anyOccluded ? MeshGroupTest.Occluded : MeshGroupTest.NoEvidence;
  }

  /**
   * True when three.js would frustum-cull `mesh` itself (frustumCulled and its world
   * bounding sphere misses the frustum). Off-screen meshes cover no pixel, so testing
   * them could only keep the section without any evidence.
   */
  private isOutsideFrustum(mesh: THREE.Mesh, localSphere: THREE.Sphere | null): boolean {
    if (mesh.frustumCulled === false || !localSphere) {
      return false;
    }
    // Instances / batches / skinned vertices are not bounded by the geometry sphere
    // (three.js frustum-tests them with their own bounds): never skip them.
    // was: if ((mesh as any).isInstancedMesh || (mesh as any).isBatchedMesh || (mesh as any).isSkinnedMesh) { — shared helper
    if (isUnboundedByGeometry(mesh)) {
      return false;
    }
    mesh.updateMatrixWorld();
    this.worldSphere.copy(localSphere).applyMatrix4(mesh.matrixWorld);
    return !this.frustum.intersectsSphere(this.worldSphere);
  }

  /**
   * Rasterize the plates of a surviving (or pinned) section that really write depth this frame,
   * unless the occluder gates say no — then the section is shown without occluding anything
   * (under-occlusion only, never a box fill):
   * - size: its box's screen rect (`rectPixels`) is below config.occluderMinRectPixels;
   * - budget: its plate triangles would push this cull's total past config.occluderTriangleBudget.
   *   Such a section is skipped and later (smaller) sections may still fit.
   * Plates over maxTrianglesPerOccluderRasterization are not counted (rasterizeMesh skips them).
   * `activeIndex` is the section's index in this cull's sorted active order (for stats.budgetExhaustedAt).
   */
  // was: (section, proxy) — no size / budget gate
  // was: (section, proxy, rectPixels) — no activeIndex
  private rasterizeSectionOccluders(section: CullSection, proxy: THREE.Group, rectPixels: number, activeIndex: number): void {
    const startTime = performance.now();
    const config = this._config;
    const maxOccluderTris = config.maxTrianglesPerOccluderRasterization;
    // No gate active (no size gate, no budget): neither gate can refuse, so the pre-pass that
    // counts the plate triangles (visibility walks) is skipped. Same result.
    if (config.occluderMinRectPixels > 0 || config.occluderTriangleBudget !== Infinity) {
      let needed = 0;
      for (const plate of section.plates) {
        if (this.isVisibleBelow(plate, proxy) && this.isValidOccluder(plate)) {
          const triangles = this.getTriangleCount(plate);
          if (triangles <= maxOccluderTris) {
            needed += triangles;
          }
        }
      }
      if (needed > 0) {
        if (rectPixels < config.occluderMinRectPixels) {
          this._stats.occludersSkippedSmall++;
          this._stats.rasterizeTimeMs += performance.now() - startTime;
          return;
        }
        if (this.occluderTrianglesUsed + needed > config.occluderTriangleBudget) {
          this._stats.occludersSkippedBudget++;
          if (this._stats.budgetExhaustedAt < 0) {
            this._stats.budgetExhaustedAt = activeIndex;
          }
          this._stats.rasterizeTimeMs += performance.now() - startTime;
          return;
        }
        this.occluderTrianglesUsed += needed;
      }
    }
    for (const plate of section.plates) {
      if (!this.isVisibleBelow(plate, proxy) || !this.isValidOccluder(plate)) {
        continue;
      }
      if (this.depthBuffer.rasterizeMesh(plate, maxOccluderTris)) {
        this._stats.occludersRasterizedGeometry++;
      } else {
        // Too many triangles (or no geometry): do NOT box-fill. A bounding-box
        // occluder is drawn larger than the real silhouette and would falsely
        // occlude visible sections behind it. Skipping it can only under-occlude.
        this._stats.occludersSkipped++;
      }
    }
    this._stats.rasterizeTimeMs += performance.now() - startTime;
  }

  cull(camera: THREE.Camera): void {
    if (!this._config.enabled) {
      // Stop path: nothing may stay hidden while culling is off.
      this.restoreAll();
      return;
    }
    if (this.backedOff && !this.takeProbe()) {
      return; // backed off: every proxy was shown when backing off, nothing to do
    }
    // was: this.runCull(camera); this.updateBackoff();
    this.updateBackoff(this.timedCull(camera));
  }

  /** runCull timed with the injectable clock (ms), for the back-off cost rule. */
  private timedCull(camera: THREE.Camera): number {
    const start = this.now();
    this.runCull(camera);
    return this.now() - start;
  }

  /** Forget the back-off window and resume normal culling (build / rebuild / clear). */
  private resetBackoff(): void {
    this.backedOff = false;
    this.backoffReason = null;
    this.windowNext = 0;
    this.windowFilled = 0;
    this.callsSinceProbe = 0;
  }

  /** While backed off: count a cull call; true on every config.backoffProbeInterval-th call. */
  private takeProbe(): boolean {
    this.callsSinceProbe++;
    if (this.callsSinceProbe < Math.max(1, this._config.backoffProbeInterval)) {
      return false;
    }
    this.callsSinceProbe = 0;
    return true;
  }

  /**
   * After a real cull (normal or probe) that took `cullMs`. Normal: add its triangles and time to
   * the window and, once the window is full, back off (show every proxy) when backoffDecision says
   * so for the window's share and median cull time. Probe: resume when backoffDecision would not
   * back off on the probe's own share and time, else show every proxy again. No candidate
   * triangles = no evidence = never back off (and a probe without candidates does not resume).
   */
  private updateBackoff(cullMs: number): void {
    const culled = this._stats.culledTriangles;
    const candidates = this._stats.candidateTriangles;

    if (this.backedOff) {
      // was: const reason = candidates > 0 ? this.backoffDecision(culled / candidates, cullMs) : this.backoffReason;
      const reason = candidates > 0 ? this.backoffDecision(culled / candidates, cullMs, this._stats.occlusionCulled) : this.backoffReason;
      if (candidates > 0 && reason === null) {
        this.resetBackoff();
        console.log(`[culling] resumed: ${cullMs.toFixed(1)} ms, ${OcclusionCuller.percent(culled, candidates)}% of triangles culled`);
      } else {
        this.backoffReason = reason; // the probe's own reason (unchanged without candidates)
        this.restoreAll();
      }
    } else {
      const size = Math.floor(this._config.backoffWindow);
      if (size > 0) {
        if (this.windowCulled.length !== size) {
          this.windowCulled = new Float64Array(size);
          this.windowCandidates = new Float64Array(size);
          this.windowSections = new Float64Array(size);
          this.windowMs = new Float64Array(size);
          this.windowMsSorted = new Float64Array(size);
          this.windowNext = 0;
          this.windowFilled = 0;
        }
        this.windowCulled[this.windowNext] = culled;
        this.windowCandidates[this.windowNext] = candidates;
        this.windowSections[this.windowNext] = this._stats.occlusionCulled;
        this.windowMs[this.windowNext] = cullMs;
        this.windowNext = (this.windowNext + 1) % size;
        this.windowFilled = Math.min(size, this.windowFilled + 1);

        if (this.windowFilled === size) {
          let sumCulled = 0;
          let sumCandidates = 0;
          let sumSections = 0;
          for (let i = 0; i < size; i++) {
            sumCulled += this.windowCulled[i];
            sumCandidates += this.windowCandidates[i];
            sumSections += this.windowSections[i];
          }
          const meanSections = sumSections / size;
          // was: if (sumCandidates > 0 && sumCulled / sumCandidates < threshold) { — share rule only
          const medianMs = this.windowMedianMs();
          // was: const reason = sumCandidates > 0 ? this.backoffDecision(sumCulled / sumCandidates, medianMs) : null;
          const reason = sumCandidates > 0 ? this.backoffDecision(sumCulled / sumCandidates, medianMs, meanSections) : null;
          if (reason !== null) {
            this.resetBackoff();
            this.backedOff = true;
            this.backoffReason = reason;
            this.restoreAll();
            // was: console.log(`[culling] backed off: ${OcclusionCuller.percent(sumCulled, sumCandidates)}% of triangles culled`);
            // was: console.log(`[culling] backed off (${reason}): ${medianMs.toFixed(1)} ms median, ` + `${OcclusionCuller.percent(sumCulled, sumCandidates)}% of triangles culled`);
            const budget = reason === 'cost'
              ? ` > ${this.cullBudgetMs(meanSections).toFixed(1)} ms budget (${Math.round(meanSections)} sections culled)`
              : '';
            console.log(`[culling] backed off (${reason}): ${medianMs.toFixed(1)} ms median${budget}, `
              + `${OcclusionCuller.percent(sumCulled, sumCandidates)}% of triangles culled`);
          }
        }
      }
    }
    this._stats.backedOff = this.backedOff;
    this._stats.backoffReason = this.backoffReason;
  }

  /**
   * The back-off rules for a culled share and a cull time (window median, or a probe's own time):
   * share at or above backoffNeverBelowShare -> keep culling (null); below backoffMinCulledFraction
   * -> 'share'; cull time above backoffMaxCullMs and share below backoffCostShareCeiling -> 'cost';
   * else keep culling (null).
   */
  // was: private backoffDecision(share: number, cullMs: number): BackoffReason | null {
  //   const config = this._config;
  //   if (share >= config.backoffNeverBelowShare) { return null; }
  //   if (share < config.backoffMinCulledFraction) { return 'share'; }
  //   if (cullMs > config.backoffMaxCullMs && share < config.backoffCostShareCeiling) { return 'cost'; }
  //   return null;
  // }
  /**
   * The back-off rules for a culled share, a cull time (window median, or a probe's own time) and
   * the sections it hid (window mean, or the probe's own): share below backoffMinCulledFraction ->
   * 'share'; cull time above cullBudgetMs(culledSections) -> 'cost'; else keep culling (null).
   */
  private backoffDecision(share: number, cullMs: number, culledSections: number): BackoffReason | null {
    if (share < this._config.backoffMinCulledFraction) {
      return 'share';
    }
    if (cullMs > this.cullBudgetMs(culledSections)) {
      return 'cost';
    }
    return null;
  }

  /** Cull time a cull may cost for the sections it hides: max(backoffMaxCullMs, sections × backoffSectionCostMs). */
  private cullBudgetMs(culledSections: number): number {
    const perSection = Number(this._config.backoffSectionCostMs);
    const saved = Number.isFinite(perSection) && perSection > 0 ? culledSections * perSection : 0;
    return Math.max(this._config.backoffMaxCullMs, saved);
  }

  /** Median of the (full) window's cull times; the mean of the two middle values for an even window. */
  private windowMedianMs(): number {
    const sorted = this.windowMsSorted;
    sorted.set(this.windowMs);
    sorted.sort();
    const n = sorted.length;
    const mid = n >> 1;
    return n % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  }

  // was: the share-only updateBackoff() (no cull time, no reason):
  // /**
  //  * After a real cull (normal or probe). Normal: add its triangles to the window and back
  //  * off (show every proxy) when the window is full and culled / candidate is below
  //  * config.backoffMinCulledFraction. No candidate triangles = no evidence = never back off.
  //  * Probe: resume at or above the threshold, else show every proxy again.
  //  */
  // private updateBackoff(): void {
  //   const threshold = this._config.backoffMinCulledFraction;
  //   const culled = this._stats.culledTriangles;
  //   const candidates = this._stats.candidateTriangles;
  //
  //   if (this.backedOff) {
  //     if (candidates > 0 && culled / candidates >= threshold) {
  //       this.resetBackoff();
  //       console.log(`[culling] resumed: ${OcclusionCuller.percent(culled, candidates)}%`);
  //     } else {
  //       this.restoreAll();
  //     }
  //   } else {
  //     const size = Math.floor(this._config.backoffWindow);
  //     if (size > 0) {
  //       if (this.windowCulled.length !== size) {
  //         this.windowCulled = new Float64Array(size);
  //         this.windowCandidates = new Float64Array(size);
  //         this.windowNext = 0;
  //         this.windowFilled = 0;
  //       }
  //       this.windowCulled[this.windowNext] = culled;
  //       this.windowCandidates[this.windowNext] = candidates;
  //       this.windowNext = (this.windowNext + 1) % size;
  //       this.windowFilled = Math.min(size, this.windowFilled + 1);
  //
  //       if (this.windowFilled === size) {
  //         let sumCulled = 0;
  //         let sumCandidates = 0;
  //         for (let i = 0; i < size; i++) {
  //           sumCulled += this.windowCulled[i];
  //           sumCandidates += this.windowCandidates[i];
  //         }
  //         if (sumCandidates > 0 && sumCulled / sumCandidates < threshold) {
  //           this.resetBackoff();
  //           this.backedOff = true;
  //           this.restoreAll();
  //           console.log(`[culling] backed off: ${OcclusionCuller.percent(sumCulled, sumCandidates)}% of triangles culled`);
  //         }
  //       }
  //     }
  //   }
  //   this._stats.backedOff = this.backedOff;
  // }

  private static percent(part: number, whole: number): string {
    return (100 * part / whole).toFixed(1);
  }

  /** The full section cull (frustum + front-to-back occlusion); writes only proxy.visible. */
  private runCull(camera: THREE.Camera): void {
    const startTime = performance.now();
    this._stats = createEmptyStats();
    this._stats.totalObjects = this.sections.length;
    // Before resetCounters and every use of the buffer, so the whole cull (and its counters) sees one buffer.
    this.syncDepthBuffer();
    this.depthBuffer.resetCounters();

    camera.updateMatrixWorld();
    this.viewProjection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.viewProjection);
    camera.getWorldPosition(this.cameraWorldPos);

    // Phase 1: source visibility + per-section sphere frustum test.
    const frustumStart = performance.now();
    const active = this.active;
    active.length = 0;
    for (const state of this.sections) {
      const proxy = state.section.proxy;
      if (!proxy || state.empty) {
        continue; // cannot hide / nothing to hide
      }
      if (!this.isSectionSourceVisible(state.section, proxy)) {
        proxy.visible = true;
        continue;
      }
      this.worldSphere.copy(state.localSphere).applyMatrix4(state.section.source.group.matrixWorld);
      if (!this.frustum.intersectsSphere(this.worldSphere)) {
        // three.js frustum-culls the meshes itself; the culler never hides for frustum.
        proxy.visible = true;
        this._stats.frustumCulled++;
        continue;
      }
      state.distSq = this.worldSphere.center.distanceToSquared(this.cameraWorldPos);
      // Nearest point of the section box to the camera, in section space (no allocation): the
      // cached inverse is refreshed only when the group's world matrix changed since it was taken.
      const m = state.section.source.group.matrixWorld;
      if (!state.worldSeen.equals(m)) {
        state.worldSeen.copy(m);
        state.inverseWorld.copy(m).invert();
      }
      this.localCam.copy(this.cameraWorldPos).applyMatrix4(state.inverseWorld);
      this.clampScratch.copy(this.localCam).clamp(state.localBox.min, state.localBox.max);
      state.nearDistSq = this.clampScratch.distanceToSquared(this.localCam);
      // Non-invertible group matrix (zero inverse, w = 0): NaN key; use the centre distance so the comparator stays consistent.
      // A zero scale already at build leaves the local box (so the centre too) NaN: then the section sorts last.
      if (!Number.isFinite(state.distSq)) {
        state.distSq = Number.MAX_VALUE;
      }
      if (!Number.isFinite(state.nearDistSq)) {
        state.nearDistSq = state.distSq;
      }
      // A box containing the camera has no nearest point: use its centre distance. Such sections cross
      // the near plane and are shown untested anyway; key 0 only let them spend the occluder triangle
      // budget before the near walls that occlude (interior views: most active sections contain the camera).
      if (state.nearDistSq === 0) {
        state.nearDistSq = state.distSq;
      }
      active.push(state);
    }
    this._stats.bvhTraversalTime = performance.now() - frustumStart;

    if (!this._config.occlusionEnabled) {
      for (const state of active) {
        state.section.proxy!.visible = true;
      }
      this._stats.visibleObjects = active.length;
    } else if (active.length > 0) {
      // Phase 2: front to back, test before rasterize. A section is tested against
      // the nearer sections that survived; only survivors write the depth buffer.
      const occlusionStart = performance.now();
      this.depthBuffer.clear();
      this.depthBuffer.updateViewProjection(camera);
      // Read every cull, so config changes (and the legacy tie-visible values) apply immediately.
      this.depthBuffer.setOccludeeDepthMargin(this._config.occludeeDepthMarginNdc, this._config.occludeeDepthMarginRelative);
      // was: active.sort((a, b) => a.distSq - b.distSq);
      // Nearest box point first (a long hull plate sloping away is tested before what it hides even when
      // its centre is farther), the sphere-centre distance as tiebreak; orderKey 'centre' is the old key.
      const byNearest = OcclusionCuller.orderKey === 'nearest';
      active.sort(byNearest
        ? (a, b) => (a.nearDistSq - b.nearDistSq) || (a.distSq - b.distSq)
        : (a, b) => a.distSq - b.distSq);
      this.occluderTrianglesUsed = 0;

      // was: for (const state of active) {
      for (let activeIndex = 0; activeIndex < active.length; activeIndex++) {
        const state = active[activeIndex];
        const section = state.section;
        const proxy = section.proxy!;
        if (this.isSectionPinned(section)) {
          // Selected (outline layer): never hidden and not a culling candidate, but its
          // plates still occlude the sections behind it, in front-to-back order
          // (same size / budget gates as any survivor).
          proxy.visible = true;
          this._stats.visibleObjects++;
          // was: this.rasterizeSectionOccluders(section, proxy);
          // was: this.rasterizeSectionOccluders(section, proxy, this.sectionRectPixels(state));
          this.rasterizeSectionOccluders(section, proxy, this.sectionRectPixels(state), activeIndex);
          continue;
        }
        const triangles = this.visibleTriangles(section, proxy);
        this._stats.candidateTriangles += triangles;

        if (this.isSectionOccluded(state, proxy)) {
          proxy.visible = false;
          this._stats.occlusionCulled++;
          this._stats.culledTriangles += triangles;
        } else {
          proxy.visible = true;
          this._stats.visibleObjects++;
          // was: this.rasterizeSectionOccluders(section, proxy);
          // was: this.rasterizeSectionOccluders(section, proxy, state.rectPixels); // rect from the coarse test
          this.rasterizeSectionOccluders(section, proxy, state.rectPixels, activeIndex); // rect from the coarse test
        }
      }

      this._stats.occlusionTestTime = performance.now() - occlusionStart;
    }

    this.recordSignature(camera);
    this.copyDepthBufferCounters();
    this._stats.frameTime = performance.now() - startTime;
  }

  /** Copy the depth buffer's work counters of this cull into the stats. */
  private copyDepthBufferCounters(): void {
    const counters = this.depthBuffer.counters;
    const stats = this._stats;
    stats.projectTimeMs = counters.projectTimeMs;
    stats.verticesProjected = counters.verticesProjected;
    stats.projectionCacheHits = counters.projectionCacheHits;
    stats.trianglesTested = counters.trianglesTested;
    stats.coverageFullCells = counters.coverageFullCells;
    stats.coverageSubSampleCells = counters.coverageSubSampleCells;
    stats.trianglesRasterized = counters.trianglesRasterized;
  }

  /** Recompute the bounds of the current sections (e.g. after their geometry changed). */
  rebuild(): void {
    // was: if (this.allObjects.length > 0) { this.bvh.build(this.allObjects, this._config.bvhLeafSize); }
    this.build(this.sections.map(state => state.section));
  }

  clear(): void {
    // was: this.bvh.clear(); this.allObjects = []; this.occluders = []; — sections replace the mesh lists
    this.restoreAll();
    this.sections = [];
    this.active.length = 0;
    this.depthBuffer.resetProjectionCache(); // drop the last projected mesh / attribute references
    this.signatureLength = 0;
    this.invalidateSignature();
    this.resetBackoff();
    this._stats = createEmptyStats();
  }

  // Superseded plate-based implementation (one mesh = one cull unit, CULLING_FLAG bookkeeping,
  // parent-hidden self-write, BVH frustum, rasterize-all-then-test). Replaced by the section
  // culler above (writes only proxy.visible). Kept for reference:
  // build(meshes: THREE.Mesh[]): void {
  //   this.allObjects = [...meshes];
  //   this.occluders = [];
  //
  //   // Occluder candidates: any mesh with a material. Whether it really occludes
  //   // depends on its current material and is decided per frame in cull().
  //   for (const mesh of this.allObjects) {
  //     // was: if (this.isValidOccluder(mesh)) {
  //     if (mesh.material) {
  //       this.occluders.push(mesh);
  //     }
  //   }
  //
  //   this.invalidateSignature();
  //   this._stats.totalObjects = this.allObjects.length;
  //   this.bvh.build(this.allObjects, this._config.bvhLeafSize);
  // }
  //
  // /**
  //  * "Source visible" means visible from app state (self + parents), excluding prior frame culling result.
  //  * If this mesh was hidden by culling in a prior frame, keep it eligible for reevaluation.
  //  */
  // private isSourceVisible(mesh: THREE.Mesh): boolean {
  //   if (!this.isParentChainVisible(mesh.parent)) {
  //     return false;
  //   }
  //   const wasCulled = mesh.userData[OcclusionCuller.CULLING_FLAG] === true;
  //   return mesh.visible || wasCulled;
  // }
  //
  // private meshSignatureBits(mesh: THREE.Mesh): number {
  //   return (mesh.visible ? 1 : 0)
  //     | (this.isSourceVisible(mesh) ? 2 : 0)
  //     | (this.isValidOccluder(mesh) ? 4 : 0);
  // }
  //
  // private signatureMatches(camera: THREE.Camera): boolean {
  //   const cam = this.lastCameraSignature;
  //   const meshes = this.lastMeshSignature;
  //   if (!cam || !meshes || meshes.length !== this.allObjects.length) {
  //     return false;
  //   }
  //   const world = camera.matrixWorld.elements;
  //   const projection = camera.projectionMatrix.elements;
  //   for (let i = 0; i < 16; i++) {
  //     if (cam[i] !== world[i] || cam[16 + i] !== projection[i]) {
  //       return false;
  //     }
  //   }
  //   for (let i = 0; i < this.allObjects.length; i++) {
  //     if (meshes[i] !== this.meshSignatureBits(this.allObjects[i])) {
  //       return false;
  //     }
  //   }
  //   return true;
  // }
  //
  // private recordSignature(camera: THREE.Camera): void {
  //   const cam = this.lastCameraSignature ?? new Float64Array(32);
  //   cam.set(camera.matrixWorld.elements, 0);
  //   cam.set(camera.projectionMatrix.elements, 16);
  //   this.lastCameraSignature = cam;
  //
  //   let meshes = this.lastMeshSignature;
  //   if (!meshes || meshes.length !== this.allObjects.length) {
  //     meshes = new Uint8Array(this.allObjects.length);
  //   }
  //   for (let i = 0; i < this.allObjects.length; i++) {
  //     meshes[i] = this.meshSignatureBits(this.allObjects[i]);
  //   }
  //   this.lastMeshSignature = meshes;
  // }
  //
  // cull(camera: THREE.Camera): void {
  //   if (!this._config.enabled) {
  //     return;
  //   }
  //
  //   const startTime = performance.now();
  //   this._stats = createEmptyStats();
  //   this._stats.totalObjects = this.allObjects.length;
  //
  //   // Update view-projection matrix
  //   camera.updateMatrixWorld();
  //   const frustum = new THREE.Frustum();
  //   const viewProjectionMatrix = new THREE.Matrix4();
  //   viewProjectionMatrix.multiplyMatrices(
  //     (camera as THREE.PerspectiveCamera).projectionMatrix,
  //     camera.matrixWorldInverse
  //   );
  //   frustum.setFromProjectionMatrix(viewProjectionMatrix);
  //
  //   // Phase 1: Hierarchical frustum culling via BVH
  //   const bvhStart = performance.now();
  //   const frustumVisible = this.bvh.traverse(frustum);
  //   const frustumVisibleSet = new Set(frustumVisible);
  //   const sourceVisibleSet = new Set<THREE.Mesh>();
  //   for (const mesh of this.allObjects) {
  //     if (this.isSourceVisible(mesh)) {
  //       sourceVisibleSet.add(mesh);
  //     } else {
  //       mesh.visible = false;
  //       mesh.userData[OcclusionCuller.CULLING_FLAG] = false;
  //     }
  //   }
  //   this._stats.bvhTraversalTime = performance.now() - bvhStart;
  //
  //   // Mark source-visible objects outside frustum as invisible
  //   for (const obj of this.allObjects) {
  //     if (sourceVisibleSet.has(obj) && !frustumVisibleSet.has(obj)) {
  //       obj.visible = false;
  //       obj.userData[OcclusionCuller.CULLING_FLAG] = true;
  //       this._stats.frustumCulled++;
  //     }
  //   }
  //
  //   const activeFrustumVisible = frustumVisible
  //     .filter(obj => sourceVisibleSet.has(obj as THREE.Mesh))
  //     .map(obj => obj as THREE.Mesh);
  //
  //   // Phase 2: Occlusion culling (if enabled)
  //   if (this._config.occlusionEnabled && activeFrustumVisible.length > 0) {
  //     const occlusionStart = performance.now();
  //     this.depthBuffer.clear();
  //     this.depthBuffer.updateViewProjection(camera);
  //
  //     // Sort occluders by distance to camera (front to back)
  //     // Use world position, not local position
  //     const cameraWorldPos = new THREE.Vector3();
  //     camera.getWorldPosition(cameraWorldPos);
  //     const tempVecA = new THREE.Vector3();
  //     const tempVecB = new THREE.Vector3();
  //     const sortedOccluders = this.occluders
  //       .filter(o => sourceVisibleSet.has(o) && frustumVisibleSet.has(o) && this.isValidOccluder(o))
  //       .sort((a, b) => {
  //         a.getWorldPosition(tempVecA);
  //         b.getWorldPosition(tempVecB);
  //         const distA = tempVecA.distanceToSquared(cameraWorldPos);
  //         const distB = tempVecB.distanceToSquared(cameraWorldPos);
  //         return distA - distB;
  //       });
  //
  //     // Rasterize occluders to the depth buffer using their actual mesh geometry.
  //     // An occluder is only allowed to occlude when its real silhouette can be drawn.
  //     const maxOccluderTris = this._config.maxTrianglesPerOccluderRasterization;
  //     for (const occluder of sortedOccluders) {
  //       if (this.depthBuffer.rasterizeMesh(occluder, maxOccluderTris)) {
  //         this._stats.occludersRasterizedGeometry++;
  //       } else {
  //         // Too many triangles (or no geometry): do NOT box-fill. A bounding-box
  //         // occluder is drawn larger than the real silhouette and would falsely
  //         // occlude visible sections behind it. Skipping it can only under-occlude.
  //         this._stats.occludersSkipped++;
  //       }
  //     }
  //
  //     const maxOccludeeTris = this._config.maxTrianglesPerOccludeeTest;
  //
  //     // Test remaining objects for occlusion. Use geometry when feasible and OBB fallback for heavy meshes.
  //     for (const mesh of activeFrustumVisible) {
  //       const triCount = this.getTriangleCount(mesh);
  //       let occluded = false;
  //
  //       if (triCount > maxOccludeeTris) {
  //         const corners = this.computeOBBCorners(mesh);
  //         occluded = this.depthBuffer.isOccludedOBB(corners);
  //         this._stats.occludeesTestedOBB++;
  //       } else {
  //         occluded = this.depthBuffer.isOccludedMesh(mesh, maxOccludeeTris);
  //         this._stats.occludeesTestedGeometry++;
  //       }
  //
  //       if (occluded) {
  //         mesh.visible = false;
  //         mesh.userData[OcclusionCuller.CULLING_FLAG] = true;
  //         this._stats.occlusionCulled++;
  //       } else {
  //         mesh.visible = true;
  //         mesh.userData[OcclusionCuller.CULLING_FLAG] = false;
  //       }
  //     }
  //
  //     this._stats.occlusionTestTime = performance.now() - occlusionStart;
  //   } else {
  //     // No occlusion culling, just mark frustum-visible as visible
  //     for (const mesh of activeFrustumVisible) {
  //       mesh.visible = true;
  //       mesh.userData[OcclusionCuller.CULLING_FLAG] = false;
  //     }
  //   }
  //
  //   this._stats.visibleObjects = this.allObjects.filter(obj => obj.visible && this.isParentChainVisible(obj.parent)).length;
  //   // Snapshot the post-cull state (mesh.visible now holds the cull result).
  //   this.recordSignature(camera);
  //   this._stats.frameTime = performance.now() - startTime;
  // }
  //
  // rebuild(): void {
  //   if (this.allObjects.length > 0) {
  //     this.bvh.build(this.allObjects, this._config.bvhLeafSize);
  //   }
  //   this.invalidateSignature();
  // }
  //
  // clear(): void {
  //   this.bvh.clear();
  //   this.allObjects = [];
  //   this.occluders = [];
  //   this.invalidateSignature();
  //   this._stats = createEmptyStats();
  // }
}
